import React from 'react';
import { Link } from 'react-router-dom';

// Get current date in New Zealand format
const getCurrentDate = () => {
  return new Date().toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="legal-container w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Terms of Service
            </h1>
            <p className="text-gray-600">
              Last updated: {getCurrentDate()}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <div className="space-y-6">
              {/* Introduction */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  Welcome to FitFlow! By accessing or using our fitness class booking application ("App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our App.
                </p>
              </section>

              

              {/* Health Disclaimer */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Health Disclaimer and Waiver</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>IMPORTANT:</strong> FitFlow provides fitness class booking services for informational purposes only. It is not a substitute for professional medical advice, diagnosis, or treatment.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Before participating in any fitness class, consult with a healthcare professional</li>
                  <li>You acknowledge that physical exercise involves inherent risks of injury</li>
                  <li>You participate in classes at your own risk and assume full responsibility for any injuries</li>
                  <li>You release FitFlow and class instructors from any liability for injuries or damages</li>
                </ul>
              </section>

              {/* User Accounts */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">4. User Accounts</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To access our services, you must create an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Keep your account information updated</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Be responsible for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                </ul>
              </section>

              {/* Class Bookings */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Class Bookings and Cancellations</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  When booking fitness classes through our App:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Bookings are subject to class availability and capacity limits</li>
                  <li>You must arrive on time for your booked classes</li>
                  <li>Late arrivals may result in forfeiture of your booking</li>
                  <li>Cancellation policies vary by class and instructor</li>
                  <li>No-shows may affect your ability to book future classes</li>
                  <li>We reserve the right to cancel classes due to unforeseen circumstances</li>
                </ul>
              </section>

              {/* User Conduct */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">6. User Conduct</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Use the App for any unlawful or prohibited purpose</li>
                  <li>Interfere with or disrupt the App's functionality</li>
                  <li>Attempt to gain unauthorized access to other accounts</li>
                  <li>Share your account credentials with others</li>
                  <li>Use the App to harass, abuse, or harm others</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
                <p className="text-gray-700 leading-relaxed">
                  All content, trademarks, and data on the App, including software, text, graphics, and logos, are the property of FitFlow or its licensors and are protected by New Zealand and international intellectual property laws. You may not reproduce, distribute, or create derivative works from any content without our express written permission.
                </p>
              </section>

              {/* Privacy */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Privacy</h2>
                <p className="text-gray-700 leading-relaxed">
                  Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, use, and protect your information when you use our App. By using our App, you consent to the collection and use of information as described in our Privacy Policy.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Termination</h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to suspend or terminate your access to the App at our discretion, without notice, if you violate these Terms or engage in conduct that we deem inappropriate. You may also terminate your account at any time by contacting us.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To the fullest extent permitted by New Zealand law:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>FitFlow shall not be liable for any indirect, incidental, or consequential damages</li>
                  <li>We are not responsible for injuries sustained during fitness classes</li>
                  <li>Our total liability shall not exceed the amount you paid us in the past 12 months</li>
                  <li>We provide the App "as is" without warranties of any kind</li>
                </ul>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Governing Law</h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms are governed by the laws of New Zealand. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the New Zealand courts. If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
                </p>
              </section>

              {/* Changes to Terms */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Changes to Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update these Terms from time to time. We will notify you of any significant changes by posting the new Terms on our App. Your continued use of the App after such changes constitutes your acceptance of the new Terms.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-gray-700">
                    <strong>Email:</strong> younitefitness@hotmail.com<br/>
                    <strong>Subject:</strong> Terms of Service Inquiry
                  </p>
                </div>
              </section>

              {/* Effective Date */}
              <section className="border-t pt-6">
                <p className="text-sm text-gray-500">
                  <strong>Effective Date:</strong> January 2025<br/>
                  <strong>Last Updated:</strong> {getCurrentDate()}
                </p>
              </section>
            </div>
          </div>

          {/* Back to Login */}
          <div className="text-center mt-8">
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              ← Back to Login
            </Link>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500">
              © 2025 FitFlow. <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link> | <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">Terms</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
